/**
 * Self-service password reset page.
 *
 * Reads ?token=... from the URL, validates input, calls POST /auth/reset-password,
 * then redirects to /login after success.
 */
import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api, { getErrorMessage } from '../services/api'
import { PasswordStrengthBar } from '../components/PasswordStrengthBar'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token') || ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Up-front link check: 'checking' while validating, 'valid' to show the form,
  // or an 'invalid' reason so we tell the user the link is dead before they bother
  // typing a new password (avoids the confusing "already used" error on submit).
  const [linkStatus, setLinkStatus] = useState<'checking' | 'valid' | 'invalid'>('checking')
  const [invalidReason, setInvalidReason] = useState<string>('')

  // Redirect to login if no token in URL; otherwise validate it (without consuming it).
  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true })
      return
    }
    let active = true
    api
      .get('/v1/auth/reset-password/validate', { params: { token } })
      .then((res) => {
        if (!active) return
        if (res.data?.valid) {
          setLinkStatus('valid')
        } else {
          setInvalidReason(res.data?.reason || 'invalid')
          setLinkStatus('invalid')
        }
      })
      .catch(() => {
        // On a network/unexpected error, fall back to showing the form — the reset
        // POST still enforces token validity server-side.
        if (active) setLinkStatus('valid')
      })
    return () => {
      active = false
    }
  }, [token, navigate])

  const invalidMessage =
    invalidReason === 'used'
      ? 'This link has already been used. For security, each link works only once — request a new one to set your password.'
      : invalidReason === 'expired'
      ? 'This link has expired. Request a new one to set your password.'
      : 'This link is no longer valid. It may be incorrect, or a newer link has replaced it. Request a new one to continue.'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await api.post('/v1/auth/reset-password', {
        token,
        new_password: newPassword,
      })
      setSuccess(true)
      // Redirect to login after 2 seconds
      setTimeout(() => navigate('/login', { replace: true }), 2000)
    } catch (err: unknown) {
      // getErrorMessage already unwraps FastAPI's `detail` (string or 422 array)
      // and falls back for network faults, so no `any` cast is needed here.
      setError(
        getErrorMessage(err) ||
          'Failed to reset password. The link may have expired — please request a new one.'
      )
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return null // useEffect will redirect
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <img src="/favicon.svg" alt="FCC License Manager" className="h-16 w-16" />
          <h1 className="mt-3 text-2xl font-bold text-gray-900 tracking-tight">
            FCC License Manager
          </h1>
          <h2 className="mt-4 text-center text-xl font-semibold text-gray-700">
            Set a new password
          </h2>
        </div>

        {linkStatus === 'checking' ? (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">Checking your link&hellip;</p>
          </div>
        ) : linkStatus === 'invalid' ? (
          <div className="mt-8 space-y-4">
            <div className="alert alert-error">{invalidMessage}</div>
            <button
              type="button"
              onClick={() => navigate('/login?forgot=1')}
              className="w-full btn btn-primary"
            >
              Request a new link
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="tap text-sm text-primary-600 hover:text-primary-500 font-medium"
              >
                Back to sign in
              </button>
            </div>
          </div>
        ) : success ? (
          <div className="mt-8 text-center space-y-4">
            <div className="alert alert-success">
              Password reset successfully. Redirecting to login&hellip;
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="newPassword" className="label">
                    New password
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="input"
                    placeholder="At least 15 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoFocus
                  />
                  <PasswordStrengthBar password={newPassword} />
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum 15 characters with uppercase, lowercase, number, and special character.
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirm new password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="input"
                    placeholder="Repeat your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting password…' : 'Reset password'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="tap text-sm text-primary-600 hover:text-primary-500 font-medium"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
